<script setup>
import { ref, onMounted, watch } from 'vue';
import schedulingAPI from '../api/scheduling';

const props = defineProps({
  initialPartners: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['saved']);

const partners = ref(
  props.initialPartners && props.initialPartners.length
    ? [...props.initialPartners]
    : []
);
const isLoading = ref(false);
const showModal = ref(false);
const isSaving = ref(false);
const editingPartner = ref(null);

watch(
  () => props.initialPartners,
  newVal => {
    if (newVal && newVal.length && !partners.value.length) {
      partners.value = [...newVal];
    }
  }
);

const formData = ref({
  id: null,
  name: '',
  contact_person: '',
  phone_number: '',
  email: '',
  commission_percentage: 0,
  notes: '',
});

const fetchPartners = async () => {
  if (partners.value.length === 0) {
    isLoading.value = true;
  }
  try {
    const res = await schedulingAPI.getPartners();
    partners.value = res.data || [];
  } catch (err) {
    console.error('Erro ao carregar parceiros:', err);
  } finally {
    isLoading.value = false;
  }
};

const openNewPartner = () => {
  editingPartner.value = null;
  formData.value = {
    id: null,
    name: '',
    contact_person: '',
    phone_number: '',
    email: '',
    commission_percentage: 0,
    notes: '',
  };
  showModal.value = true;
};

const openEditPartner = p => {
  editingPartner.value = p;
  formData.value = {
    id: p.id,
    name: p.name || '',
    contact_person: p.contact_person || '',
    phone_number: p.phone_number || '',
    email: p.email || '',
    commission_percentage: p.commission_percentage || 0,
    notes: p.notes || '',
  };
  showModal.value = true;
};

const savePartner = async () => {
  if (!formData.value.name.trim()) {
    alert('Por favor, informe o nome do parceiro/convênio.');
    return;
  }

  isSaving.value = true;
  try {
    if (formData.value.id) {
      await schedulingAPI.updatePartner(formData.value.id, formData.value);
    } else {
      await schedulingAPI.createPartner(formData.value);
    }
    showModal.value = false;
    await fetchPartners();
    emit('saved');
  } catch (err) {
    alert('Erro ao salvar parceiro.');
  } finally {
    isSaving.value = false;
  }
};

const deletePartner = async p => {
  if (!confirm(`Tem certeza que deseja remover o parceiro "${p.name}"?`)) return;

  try {
    await schedulingAPI.deletePartner(p.id);
    await fetchPartners();
    emit('saved');
  } catch (err) {
    alert('Erro ao remover parceiro.');
  }
};

onMounted(() => {
  if (partners.value.length === 0) {
    fetchPartners();
  }
});
</script>

<template>
  <div class="space-y-5 select-none">
    <div class="flex items-center justify-between">
      <div>
        <h2 class="text-base font-semibold text-n-slate-12">Parceiros & Convênios</h2>
        <p class="text-xs text-n-slate-11">
          Gerencie indicações, parcerias comerciais e comissões.
        </p>
      </div>

      <button
        type="button"
        class="h-8 px-3 rounded-lg text-xs font-medium text-white bg-brand hover:bg-brand-hover active:bg-brand transition-all flex items-center gap-1.5 cursor-pointer shadow-xs"
        @click="openNewPartner"
      >
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        <span>Novo Parceiro</span>
      </button>
    </div>

    <div v-if="isLoading" class="flex justify-center items-center py-16">
      <div class="animate-spin rounded-full h-7 w-7 border-2 border-brand border-t-transparent"></div>
    </div>

    <div
      v-else-if="partners.length === 0"
      class="p-12 text-center border border-dashed border-n-weak rounded-xl bg-n-surface-1"
    >
      <div class="w-12 h-12 rounded-xl bg-n-alpha-2 text-n-brand flex items-center justify-center mx-auto mb-3">
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      </div>
      <h3 class="text-sm font-semibold text-n-slate-12 mb-1">Nenhum parceiro cadastrado</h3>
      <p class="text-xs text-n-slate-11 max-w-sm mx-auto mb-4">
        Cadastre parceiros, clínicas conveniadas e médicos parceiros para rastrear indicações.
      </p>
      <button
        type="button"
        class="h-8 px-3 rounded-lg text-xs font-medium text-white bg-brand hover:bg-brand-hover transition-all cursor-pointer shadow-xs inline-flex items-center gap-1.5"
        @click="openNewPartner"
      >
        <span>Cadastrar Primeiro Parceiro</span>
      </button>
    </div>

    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5">
      <div
        v-for="p in partners"
        :key="p.id"
        class="p-4 rounded-xl bg-n-surface-1 border border-n-weak hover:border-n-weak/80 transition-all shadow-xs flex flex-col justify-between"
      >
        <div>
          <div class="flex items-start justify-between gap-3 mb-2">
            <h3 class="text-sm font-semibold text-n-slate-12 leading-tight">
              {{ p.name }}
            </h3>
            <button
              type="button"
              class="h-7 px-2.5 text-xs font-medium text-n-slate-11 hover:text-n-slate-12 bg-n-alpha-1 hover:bg-n-alpha-2 border border-n-weak rounded-lg transition-colors cursor-pointer"
              @click="openEditPartner(p)"
            >
              Editar
            </button>
          </div>

          <div class="space-y-1 text-xs text-n-slate-11 mb-3">
            <div v-if="p.contact_person">Responsável: <strong class="text-n-slate-12 font-medium">{{ p.contact_person }}</strong></div>
            <div v-if="p.phone_number">📱 {{ p.phone_number }}</div>
            <div v-if="p.email">📧 {{ p.email }}</div>
            <div v-if="p.commission_percentage > 0" class="text-emerald-500 font-semibold">
              Comissão: {{ p.commission_percentage }}%
            </div>
          </div>
        </div>

        <div class="pt-2.5 border-t border-n-weak flex items-center justify-between">
          <button
            type="button"
            class="text-xs font-medium text-rose-500 hover:text-rose-600 dark:hover:text-rose-400 transition-colors cursor-pointer"
            @click="deletePartner(p)"
          >
            Remover
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Novo / Editar Parceiro -->
    <div
      v-if="showModal"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fade-in"
      @click.self="showModal = false"
    >
      <div
        class="w-full max-w-md bg-n-surface-1 border border-n-weak rounded-2xl shadow-2xl overflow-hidden text-n-slate-12"
      >
        <div class="px-5 py-3.5 border-b border-n-weak flex items-center justify-between bg-n-alpha-1">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ formData.id ? 'Editar Parceiro' : 'Novo Parceiro / Convênio' }}
          </h3>
          <button
            type="button"
            class="p-1 rounded-md text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 cursor-pointer transition-colors"
            @click="showModal = false"
          >
            ✕
          </button>
        </div>

        <div class="p-5 space-y-3.5">
          <div>
            <label class="block text-xs font-medium text-n-slate-11 mb-1">Nome do Parceiro <span class="text-rose-500">*</span></label>
            <input
              v-model="formData.name"
              type="text"
              placeholder="Ex: Clínica ABC, Dr. Indicação..."
              class="w-full h-8 px-3 text-xs bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-10 focus:border-brand focus:outline-none transition-colors"
            />
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">Pessoa de Contato</label>
              <input
                v-model="formData.contact_person"
                type="text"
                placeholder="Ex: Dra. Mariana"
                class="w-full h-8 px-2.5 text-xs bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-10 focus:border-brand focus:outline-none transition-colors"
              />
            </div>
            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">Comissão (%)</label>
              <input
                v-model.number="formData.commission_percentage"
                type="number"
                step="0.1"
                min="0"
                max="100"
                class="w-full h-8 px-2.5 text-xs bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-10 focus:border-brand focus:outline-none transition-colors"
              />
            </div>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">Telefone</label>
              <input
                v-model="formData.phone_number"
                type="text"
                class="w-full h-8 px-2.5 text-xs bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-10 focus:border-brand focus:outline-none transition-colors"
              />
            </div>
            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">E-mail</label>
              <input
                v-model="formData.email"
                type="email"
                class="w-full h-8 px-2.5 text-xs bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-10 focus:border-brand focus:outline-none transition-colors"
              />
            </div>
          </div>
        </div>

        <div class="px-5 py-3 border-t border-n-weak bg-n-alpha-1 flex items-center justify-end gap-2">
          <button
            type="button"
            class="h-8 px-3 text-xs font-medium text-n-slate-11 hover:text-n-slate-12 bg-n-alpha-2 hover:bg-n-alpha-3 rounded-lg transition-colors cursor-pointer"
            @click="showModal = false"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="h-8 px-4 text-xs font-medium text-white bg-brand hover:bg-brand-hover rounded-lg shadow-xs cursor-pointer disabled:opacity-50 transition-all"
            :disabled="isSaving"
            @click="savePartner"
          >
            {{ isSaving ? 'Salvando...' : 'Salvar Parceiro' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
